import { ParticipantStatus } from '@/features/dashboard/participants/types/ParticipantStatus.ts';
import { BookingDetailsDto } from '@/features/dashboard/bookings/types/BookingDetailsDto.ts';
import { PersonTargetType, TargetType } from '@/types/TargetType.ts';
import { BookingBalanceDto } from '@/features/dashboard/balances/types/BookingBalanceDto.ts';
import { PlannedTransactionPaymentStatus } from '@/features/public/bookingSummary/types/PlannedTransactionPaymentStatus.ts';
import {
  BookingBalanceVm,
  BookingSummaryVm,
  RelatedPersonVm,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import {
  buildPersonIdentity,
  CLIENT_ROLE_DETAIL,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.utils.ts';
import { personScreenLink } from '@/features/dashboard/bookingChanges/routes/detailsLinks.ts';
import { formatDateRange } from '@/features/dashboard/bookingChanges/utils/formatters.ts';

/**
 * `BookingDetailsDto` → karty panelu bocznego: „Stan rezerwacji”
 * i „Powiązane osoby”.
 *
 * Nazwy salda są backendowe i znaczą co innego, niż sugerowałby ekran:
 * `expected` to suma żywych pozycji rachunku (czyli wartość rezerwacji),
 * `outstanding` to `expected − paid`, więc przy nadpłacie bywa ujemne.
 */

/**
 * Zaległość to **istnienie raty ze statusem `OVERDUE`**, nie dodatnia reszta do
 * zapłaty: klient przed terminem pierwszej raty jest coś winien, ale nie zalega.
 * Stanu nie liczymy sami — `PlannedTransactionStatusCalculator` pokrywa raty
 * kumulatywnie sumą wpłat, więc porównanie `dueDate` z dzisiejszą datą dałoby
 * inny wynik przy nadpłacie zamykającej wcześniejsze raty.
 */
const isOverdue = (dto: BookingDetailsDto): boolean =>
  dto.installments.some(
    (installment) =>
      installment.paymentStatus === PlannedTransactionPaymentStatus.OVERDUE,
  );

const toBalanceVm = (dto: BookingBalanceDto): BookingBalanceVm => ({
  totalValue: dto.balance.expected,
  paid: dto.balance.paid,
  due: dto.balance.outstanding,
});

/**
 * Dopisek po kropce w etykiecie roli. Wynika z samej roli, więc kontrakt go nie
 * niesie — uczestnik nie ma stałego dopisku, bo jego wiek stoi w rachunku.
 */
const ROLE_DETAIL: Record<PersonTargetType, string | null> = {
  [TargetType.CLIENT]: CLIENT_ROLE_DETAIL,
  [TargetType.PARTICIPANT]: null,
};

interface RelatedPersonInput {
  id: string;
  kind: PersonTargetType;
  firstName: string;
  lastName: string;
  withdrawn?: boolean;
}

const toRelatedPerson = (
  person: RelatedPersonInput,
  currentPersonId?: string,
): RelatedPersonVm => {
  const isCurrent = person.id === currentPersonId;
  return {
    person: buildPersonIdentity({
      ...person,
      roleDetail: ROLE_DETAIL[person.kind],
    }),
    // Osoba, na której ekranie stoimy, nie dostaje odnośnika — nie ma dokąd
    // z niej prowadzić.
    link: isCurrent ? undefined : personScreenLink(person.kind, person.id),
    isCurrent: isCurrent || undefined,
    withdrawn: person.withdrawn || undefined,
  };
};

export const bookingDetailsMapper = {
  toBookingSummary(dto: BookingDetailsDto): BookingSummaryVm {
    const overdue = isOverdue(dto);

    return {
      bookingId: dto.bookingId,
      clientId: dto.client.id,
      bookingCode: dto.bookingCode,
      createdAt: dto.createdAt,
      tourName: dto.tourName,
      dateRangeLabel: formatDateRange(dto.tourStartDate, dto.tourEndDate),
      balances: dto.balances.map(toBalanceVm),
      statusLabel: overdue ? 'Zaległość' : 'W normie',
      statusTone: overdue ? 'error' : 'success',
      bookingStatus: dto.status,
    };
  },

  /**
   * Karta „Powiązane osoby”. Obsadę rezerwacji niesie sama metryczka — osobnego
   * odczytu rekordów powiązanych nie ma i nie będzie.
   *
   * Zapisujący stoi pierwszy: to on jest właścicielem rezerwacji, a uczestnicy
   * idą w kolejności, w której zwraca ich backend.
   */
  toRelatedPeople(
    dto: BookingDetailsDto,
    currentPersonId?: string,
  ): RelatedPersonVm[] {
    return [
      toRelatedPerson(
        { ...dto.client, kind: TargetType.CLIENT },
        currentPersonId,
      ),
      ...dto.participants.map((participant) =>
        toRelatedPerson(
          {
            ...participant,
            kind: TargetType.PARTICIPANT,
            withdrawn: participant.status === ParticipantStatus.WITHDRAWN,
          },
          currentPersonId,
        ),
      ),
    ];
  },
};
